import React, { useState, useEffect, useCallback } from 'react'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './AuthContext'
import PortChecker from './components/PortChecker'
import PingTool from './components/PingTool'
import HttpTester from './components/HttpTester'
import TelnetTool from './components/TelnetTool'
import WsTool from './components/WsTool'
import DnsLookup from './components/DnsLookup'
import SslChecker from './components/SslChecker'
import AdminPanel from './components/AdminPanel'
import LoginModal from './components/LoginModal'
import api from './api'
import {
  Wifi, Shield, Terminal, Globe, Activity,
  LogOut, LogIn, ChevronRight, Zap, Radio, Hash, Users, Lock, Menu
} from 'lucide-react'

const BASE_NAV = [
  { id: 'port',   icon: Shield,   label: 'Kiểm tra Port',  desc: 'TCP/UDP port scanner' },
  { id: 'ping',   icon: Activity, label: 'Ping / Monitor', desc: 'Ping liên tục & log' },
  { id: 'http',   icon: Globe,    label: 'HTTP Request',   desc: 'REST API như Postman' },
  { id: 'telnet', icon: Terminal, label: 'Telnet / TCP',   desc: 'Kết nối TCP thô' },
  { id: 'ws',     icon: Radio,    label: 'WebSocket',      desc: 'Kiểm tra WS endpoint' },
  { id: 'dns',    icon: Hash,     label: 'DNS Lookup',     desc: 'Tra cứu bản ghi DNS' },
  { id: 'ssl',    icon: Lock,     label: 'SSL Inspector',  desc: 'Cert chain & expiry' },
]

function AppInner() {
  const { user, logout } = useAuth()
  const [tab, setTab] = useState('port')
  const [wanIp, setWanIp] = useState('')
  const [showLogin, setShowLogin] = useState(false)

  // isMobile: true khi màn hình < 768px
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  // Sidebar: desktop mặc định mở, mobile mặc định đóng (drawer)
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768)

  useEffect(() => {
    api.get('/my-ip').then(r => setWanIp(r.data.ip)).catch(() => {})
  }, [])

  // Theo dõi resize để cập nhật isMobile
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e) => {
      setIsMobile(e.matches)
      // Khi chuyển sang desktop → tự mở sidebar
      if (!e.matches) setSidebarOpen(true)
      // Khi chuyển sang mobile → đóng sidebar
      else setSidebarOpen(false)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Click nav: mobile thì đóng drawer sau khi chọn
  const handleNavClick = useCallback((id) => {
    setTab(id)
    if (isMobile) setSidebarOpen(false)
  }, [isMobile])

  const nav = user?.role === 'admin'
    ? [...BASE_NAV, { id: 'admin', icon: Users, label: 'Quản lý Admin', desc: 'Quản lý tài khoản' }]
    : BASE_NAV

  const renderTool = () => {
    const props = { wanIp, user }
    switch (tab) {
      case 'port':   return <PortChecker {...props} />
      case 'ping':   return <PingTool    {...props} />
      case 'http':   return <HttpTester  {...props} />
      case 'telnet': return <TelnetTool  {...props} />
      case 'ws':     return <WsTool      {...props} />
      case 'dns':    return <DnsLookup   {...props} />
      case 'ssl':    return <SslChecker  {...props} />
      case 'admin':  return <AdminPanel  {...props} />
      default:       return <PortChecker {...props} />
    }
  }

  return (
    <div className="flex min-h-screen grid-bg">
      {/* Status indicator */}
      <div className="status-dots">
        <div className="status-dot" />
        <div className="status-dot" />
        <div className="status-dot" />
      </div>

      {/* ── Backdrop (mobile only) ── */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`app-sidebar${isMobile ? (sidebarOpen ? ' sidebar-mobile-open' : '') : ''}`}
        style={{
          width: isMobile ? 240 : (sidebarOpen ? 240 : 60),
          transition: 'width .3s, transform .3s cubic-bezier(.4,0,.2,1)',
          flexShrink: 0,
          borderRight: '1px solid var(--border)',
          background: 'var(--bg2)',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {/* Logo */}
        <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, var(--accent2), var(--accent3))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 16px rgba(56,189,248,.3)'
            }}>
              <Zap size={18} color="#000" />
            </div>
            {(sidebarOpen || isMobile) && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  TranAnhWebTool
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Công cụ phân tích mạng</div>
              </div>
            )}
          </div>
        </div>

        {/* WAN IP */}
        {(sidebarOpen || isMobile) && wanIp && (
          <div style={{ margin: '12px 12px 0', padding: 12, borderRadius: 8, background: 'var(--panel)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Wifi size={11} /> IP WAN của bạn
            </div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{wanIp}</div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: 10, marginTop: 8, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nav.map(({ id, icon: Icon, label, desc }) => {
            const showLabel = sidebarOpen || isMobile
            return (
              <div key={id} onClick={() => handleNavClick(id)} title={!showLabel ? label : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: showLabel ? '9px 11px' : '10px',
                  borderRadius: 8, cursor: 'pointer',
                  background: tab === id ? 'rgba(56,189,248,.1)' : 'transparent',
                  color: tab === id ? 'var(--accent)' : 'var(--text2)',
                  border: tab === id ? '1px solid rgba(56,189,248,.25)' : '1px solid transparent',
                  transition: 'all .18s',
                  justifyContent: showLabel ? 'flex-start' : 'center',
                }}
                onMouseEnter={e => { if (tab !== id) { e.currentTarget.style.background = 'var(--panel)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (tab !== id) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text2)'; } }}
              >
                <Icon size={17} style={{ flexShrink: 0 }} />
                {showLabel && (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{desc}</div>
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* User / Login */}
        <div style={{ padding: 10, borderTop: '1px solid var(--border)' }}>
          {user ? (
            <>
              {(sidebarOpen || isMobile) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--panel)', marginBottom: 8 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, var(--accent2), var(--accent3))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#000' }}>{user.username[0].toUpperCase()}</span>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.username}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{user.role === 'admin' ? '👑 Admin' : '👤 User'}</div>
                  </div>
                </div>
              )}
              <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', fontSize: 13 }} onClick={logout}>
                <LogOut size={14} />
                {(sidebarOpen || isMobile) && 'Đăng xuất'}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 13 }} onClick={() => setShowLogin(true)}>
              <LogIn size={14} />
              {(sidebarOpen || isMobile) && 'Đăng nhập'}
            </button>
          )}

          {/* Collapse toggle — chỉ hiện trên desktop */}
          {!isMobile && (
            <button onClick={() => setSidebarOpen(v => !v)}
              style={{
                marginTop: 6, width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '6px', borderRadius: 7,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text3)', transition: 'all .18s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--panel)'; e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text3)'; }}
              title={sidebarOpen ? 'Thu gọn sidebar' : 'Mở rộng sidebar'}
            >
              <ChevronRight size={15} style={{ transform: sidebarOpen ? 'rotate(180deg)' : 'none', transition: 'transform .3s' }} />
            </button>
          )}
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="app-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Header */}
        <header style={{
          height: 52, borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', background: 'rgba(13,21,37,.7)', backdropFilter: 'blur(4px)',
          position: 'sticky', top: 0, zIndex: 10,
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {/* Hamburger — chỉ hiện trên mobile */}
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(v => !v)}
              style={{
                display: 'none', // CSS override để show trên mobile
                alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: 7, flexShrink: 0,
                background: 'transparent', border: '1px solid var(--border)',
                cursor: 'pointer', color: 'var(--text2)',
              }}
              title="Mở menu"
            >
              <Menu size={18} />
            </button>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nav.find(n => n.id === tab)?.label ?? 'Công cụ'}
            </div>
          </div>
          {/* Thông tin liên hệ — ẩn trên mobile */}
          <div className="header-contacts" style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: 'var(--text3)', flexShrink: 0 }}>
            <span>📞 +84 985 939 455</span>
            <span>✉️ hotro@pee.vn</span>
            <span style={{ color: 'var(--accent3)', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>webtool.pee.vn</span>
          </div>
        </header>

        <div className="main-content-pad" style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
          <div className="fade-in">
            {renderTool()}
          </div>
        </div>
      </main>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--panel2)',
            color: 'var(--text)',
            border: '1px solid var(--border2)',
            fontSize: '14px',
            fontFamily: "'Inter', sans-serif",
          },
        }}
      />
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  )
}
